import { useState } from 'react';
import { API_URL, API_VERSION, SITE_URLS } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useCustomNavigate } from '@hooks/useCustomNavigate';
import { getSessionId } from '@utils/session';

const ENDPOINT_URL = '/prd-products/{uuid}';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type GetProductDetailInput = paths[url]['get']['parameters']['path'];

export interface GetProductDetailSuccessResponse {
    data: paths[url]['get']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to get a product.
 */
export const useGetProductDetail = () => {
    const { navigate } = useCustomNavigate();
    const { getHeaders, getCommonParameters, handleCommonErrors } = useApiConfig();
    const { setProductDetail, login } = useData();
    const [loading, setLoading] = useState<boolean>(false);

    const request = async (props: GetProductDetailInput) => {
        setLoading(true);

        const commonQueryString = 'language=' + getCommonParameters().language;

        let sessionQueryString = '';
        if (login === null) {
            const sessionId = getSessionId();
            sessionQueryString = `&session_id=${encodeURIComponent(sessionId)}`;
        }

        const fullQueryString = `${commonQueryString}${sessionQueryString}`;

        try {
            const url = `${API_URL}${ENDPOINT_URL.replace('{uuid}', props.uuid || '')}`;

            const fullUrl = `${url}?${fullQueryString}`;

            const response = await fetch(fullUrl, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetProductDetailSuccessResponse = await response.json();

            setProductDetail(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);

            if (err.statusCode === 404) {
                // redirect to 404 page
                navigate(`/${SITE_URLS.pageNotFound}`);
            }
        } finally {
            setLoading(false);
        }
    };

    return { request, loading };
};
